import { serverUrl, getDefaultHeaders } from 'services/authService';
import {
  ApiToken,
  CreateApiTokenRequest,
  UpdateApiTokenRequest,
} from 'models/ApiToken';

type ProblemDetails = {
  message?: string;
  detail?: {
    validation_errors?: Record<string, string[]>;
    fields_with_errors?: string[];
  };
};

const parseErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  try {
    const errorData: ProblemDetails = await response.json();
    return errorData.message ?? fallback;
  } catch {
    return fallback;
  }
};

const parseError = async (response: Response, fallback: string): Promise<Error> => {
  try {
    const errorData: ProblemDetails = await response.json();
    const err = new Error(errorData.message ?? fallback) as Error & {
      validationErrors?: Record<string, string[]>;
    };
    if (errorData.detail?.validation_errors) {
      err.validationErrors = errorData.detail.validation_errors;
    }
    return err;
  } catch {
    return new Error(fallback);
  }
};

const fetchProfile = async (accessToken: string) => {
  const response = await fetch(`${serverUrl}/profile`, {
    headers: getDefaultHeaders(accessToken),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch customer profile');
  }

  return response.json();
};

const updateProfile = async (profileData: any, accessToken: string) => {
  const response = await fetch(`${serverUrl}/profile`, {
    method: 'PUT',
    headers: getDefaultHeaders(accessToken),
    body: JSON.stringify(profileData),
  });

  if (!response.ok) {
    let errorMessage = 'Failed to update customer profile';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.detail || errorMessage;
      console.error('Profile update error:', errorData);
    } catch (e) {
      console.error('Profile update error response:', response.status, response.statusText);
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

const fetchConnectorGroups = async (accessToken: string) => {
  const response = await fetch(`${serverUrl}/connector_groups`, {
    headers: getDefaultHeaders(accessToken),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch connector groups');
  }

  return response.json();
};

const fetchConnectors = async (accessToken: string) => {
  const response = await fetch(`${serverUrl}/connectors`, {
    headers: getDefaultHeaders(accessToken),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch connectors');
  }

  return response.json();
};

const fetchMonthlySummary = async (accessToken: string) => {
  const response = await fetch(`${serverUrl}/monthly_invoice_summary`, {
    headers: getDefaultHeaders(accessToken),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch monthly summary');
  }

  return response.json();
};

const fetchWeeklySummary = async (accessToken: string) => {
  const response = await fetch(`${serverUrl}/weekly_invoice_summary`, {
    headers: getDefaultHeaders(accessToken),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch weekly summary');
  }

  return response.json();
};

const fetchFileStoreConnection = async (accessToken: string): Promise<any[]> => {
  const response = await fetch(`${serverUrl}/get_file_store_connection_secret`, {
    headers: getDefaultHeaders(accessToken),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch file store connection');
  }

  return response.json();
};



const fetchConnectorStatusReport = async (accessToken: string) => {
  const response = await fetch(`${serverUrl}/connector_status_report`, {
    headers: getDefaultHeaders(accessToken),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch connector status report');
  }

  return response.json();
};

const fetchConnectorStatusKpi = async (accessToken: string) => {
  const response = await fetch(`${serverUrl}/connector_status_kpis`, {
    headers: getDefaultHeaders(accessToken),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch connector status kpis');
  }

  return response.json();
};

const fetchDownloadEfficacyReport = async (accessToken: string) => {
  const response = await fetch(`${serverUrl}/download_efficacy_report`, {
    headers: getDefaultHeaders(accessToken),
  });

  if (!response.ok) {
    throw new Error('Failed to download efficacy report');
  }

  return response.json();
};


const fetchWarehouseConnection = async (accessToken: string): Promise<any[]> => {
  const response = await fetch(`${serverUrl}/get_managed_warehouse_connection_secret`, {
    headers: getDefaultHeaders(accessToken),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch warehouse connection');
  }

  return response.json();
};

type SearchInvoicesParams = {
  startDate: string;
  endDate: string;
  nextPageToken?: string;
  limit?: number;
};

export type InvoiceSearchPagination = {
  has_next_page: boolean;
  next_page_token?: string;
};

export type InvoiceSearchResult = {
  invoices: any[];
  pagination: InvoiceSearchPagination;
};

const INVOICE_SEARCH_PAGE_SIZE = 100;
const INVOICE_SEARCH_FIELDS = [
  'invoice_number',
  'invoice_date',
  'file_format',
  'file',
  'company_id',
  'credential_id',
  'carrier',
];

const fetchSearchInvoices = async (
  { startDate, endDate, nextPageToken, limit = INVOICE_SEARCH_PAGE_SIZE }: SearchInvoicesParams,
  accessToken: string
): Promise<InvoiceSearchResult> => {
  const body: Record<string, unknown> = {
    start_date: startDate,
    end_date: endDate,
    output_format: 'csv',
    limit,
    fields: INVOICE_SEARCH_FIELDS,
  };
  if (nextPageToken) {
    body.next_page_token = nextPageToken;
  }

  const response = await fetch(`${serverUrl}/search_invoices`, {
    method: 'POST',
    headers: getDefaultHeaders(accessToken),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch invoices: ${response.status} ${response.statusText}`);
  }

  const payload = await response.json();
  const rawInvoices = Array.isArray(payload?.invoices) ? payload.invoices : [];
  const invoices = rawInvoices.map((invoice: any) => ({
    ...invoice,
    connector_id: invoice.connector_id ?? invoice.credential_id,
    connector_group_id: invoice.connector_group_id ?? invoice.company_id,
  }));

  return {
    invoices,
    pagination: {
      has_next_page: Boolean(payload?.pagination?.has_next_page),
      next_page_token: payload?.pagination?.next_page_token,
    },
  };
};

const fetchFileFormats = async (accessToken: string): Promise<string[]> => {
  const response = await fetch(`${serverUrl}/file_formats`, {
    headers: getDefaultHeaders(accessToken),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch file formats');
  }

  const payload = await response.json();

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.formats)) return payload.formats;
  if (Array.isArray(payload?.file_formats)) return payload.file_formats;

  return [];
};

const listApiTokens = async (accessToken: string): Promise<ApiToken[]> => {
  const response = await fetch(`${serverUrl}/api_token`, {
    headers: getDefaultHeaders(accessToken),
  });

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response, 'Failed to fetch API tokens'));
  }

  return response.json();
};

const createApiToken = async (
  data: CreateApiTokenRequest,
  accessToken: string,
): Promise<ApiToken> => {
  const response = await fetch(`${serverUrl}/api_token`, {
    method: 'POST',
    headers: getDefaultHeaders(accessToken),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw await parseError(response, 'Failed to create API token');
  }

  const result = await response.json();
  return result.api_token;
};

const updateApiToken = async (
  id: number,
  data: UpdateApiTokenRequest,
  accessToken: string,
): Promise<ApiToken> => {
  const response = await fetch(`${serverUrl}/api_token/${id}`, {
    method: 'PUT',
    headers: getDefaultHeaders(accessToken),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw await parseError(response, 'Failed to update API token');
  }

  const result = await response.json();
  return result.api_token;
};

const deleteApiToken = async (
  id: number,
  accessToken: string,
): Promise<{ message: string }> => {
  const response = await fetch(`${serverUrl}/api_token/${id}`, {
    method: 'DELETE',
    headers: getDefaultHeaders(accessToken),
  });

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response, 'Failed to delete API token'));
  }

  const text = await response.text();
  return text ? JSON.parse(text) : { message: 'API token deleted!' };
};

export {
  fetchProfile,
  updateProfile,
  fetchConnectorGroups,
  fetchConnectors,
  fetchMonthlySummary,
  fetchWeeklySummary,
  fetchFileStoreConnection,
  fetchConnectorStatusReport,
  fetchWarehouseConnection,
  fetchConnectorStatusKpi,
  fetchDownloadEfficacyReport,
  fetchSearchInvoices,
  fetchFileFormats,
  listApiTokens,
  createApiToken,
  updateApiToken,
  deleteApiToken,
};
